import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import AuthShell from "@/components/AuthShell";
import GoogleButton from "@/components/GoogleButton";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { track } from "@/lib/analytics";

const inputCls =
  "w-full rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-primary";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || params.get("returnTo") || "/app";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      track("login", { method: "email" });
      toast.success("Welcome back!");
      navigate(next);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo title="Login" description="Log in to your StUpEco Workspace account." path="/login" />
      <AuthShell
        title="Welcome back"
        subtitle="Log in to your StUpEco Workspace account."
        footer={<>New here? <Link to="/signup" className="font-medium text-foreground hover:underline" data-testid="go-signup-link">Start free</Link></>}
      >
        <GoogleButton label="Log in with Google" />
        <div className="my-6 flex items-center gap-4 text-xs text-muted-foreground">
          <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div>
            <label className="mb-1.5 block text-sm font-medium">Email</label>
            <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="login-email-input" placeholder="you@company.com" />
          </div>
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="block text-sm font-medium">Password</label>
              <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-foreground" data-testid="forgot-password-link">Forgot password?</Link>
            </div>
            <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="login-password-input" placeholder="••••••••" />
          </div>
          <button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full rounded-full bg-foreground px-6 py-3.5 text-sm font-semibold text-background transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60">
            {loading ? "Logging in…" : "Log in"}
          </button>
        </form>
      </AuthShell>
    </>
  );
}
